package br.com.vvrefeicoes.portal.core.ui.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.DatePicker
import androidx.compose.material3.DatePickerDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import com.composables.icons.lucide.Calendar
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.X
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.date_picker_cancel
import portalvv.shared.generated.resources.date_picker_confirm
import portalvv.shared.generated.resources.date_picker_none
import portalvv.shared.generated.resources.date_picker_placeholder

private const val MILLIS_PER_DAY = 86_400_000L

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AppDatePickerField(
    value: LocalDate?,
    onValueChange: (LocalDate?) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    placeholder: String = stringResource(Res.string.date_picker_placeholder),
    optional: Boolean = false,
    bottomBorderOnly: Boolean = false,
    borderColor: Color = MaterialTheme.colorScheme.outlineVariant,
    horizontalPadding: Dp = AppSpacing.space12,
) {
    var expanded by remember { mutableStateOf(false) }

    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Surface(
            onClick = { expanded = true },
            shape = AppShapes.medium,
            color = Color.Transparent,
            border = if (bottomBorderOnly) null else BorderStroke(1.dp, borderColor),
            modifier = Modifier.fillMaxWidth().height(48.dp),
        ) {
            Box(modifier = Modifier.fillMaxSize()) {
                Row(
                    modifier = Modifier.fillMaxSize().padding(horizontal = horizontalPadding),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
                ) {
                    Icon(
                        imageVector = Lucide.Calendar,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                    Text(
                        text = value?.toDisplayDate() ?: placeholder,
                        style = MaterialTheme.typography.bodyMedium,
                        color =
                            if (value == null) {
                                MaterialTheme.colorScheme.onSurfaceVariant
                            } else {
                                MaterialTheme.colorScheme.onSurface
                            },
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f),
                    )
                    if (optional && value != null) {
                        IconButton(onClick = { onValueChange(null) }, modifier = Modifier.size(24.dp)) {
                            Icon(
                                imageVector = Lucide.X,
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.onSurfaceVariant,
                                modifier = Modifier.size(16.dp),
                            )
                        }
                    }
                }
                if (bottomBorderOnly) {
                    Box(
                        modifier =
                            Modifier
                                .align(Alignment.BottomCenter)
                                .fillMaxWidth()
                                .height(1.dp)
                                .background(borderColor),
                    )
                }
            }
        }
    }

    if (expanded) {
        val pickerState = rememberDatePickerState(initialSelectedDateMillis = value?.toUtcMillis())
        DatePickerDialog(
            onDismissRequest = { expanded = false },
            confirmButton = {
                TextButton(
                    onClick = {
                        pickerState.selectedDateMillis?.let { onValueChange(it.toUtcDate()) }
                        expanded = false
                    },
                    enabled = pickerState.selectedDateMillis != null,
                ) {
                    Text(stringResource(Res.string.date_picker_confirm))
                }
            },
            dismissButton = {
                TextButton(onClick = { expanded = false }) {
                    Text(stringResource(Res.string.date_picker_cancel))
                }
            },
        ) {
            DatePicker(
                state = pickerState,
                headline = {
                    Text(
                        text =
                            pickerState.selectedDateMillis?.let { it.toUtcDate().toDisplayDate() }
                                ?: stringResource(Res.string.date_picker_none),
                        style = MaterialTheme.typography.headlineSmall,
                        modifier = Modifier.padding(start = 24.dp, top = 16.dp, bottom = 12.dp),
                    )
                },
            )
        }
    }
}

private fun LocalDate.toUtcMillis(): Long = toEpochDays() * MILLIS_PER_DAY

private fun Long.toUtcDate(): LocalDate = LocalDate.fromEpochDays((this + MILLIS_PER_DAY / 2) / MILLIS_PER_DAY)

private fun LocalDate.toDisplayDate(): String {
    val day = day.toString().padStart(2, '0')
    val month = (month.ordinal + 1).toString().padStart(2, '0')
    return "$day/$month/$year"
}
